<template>
  <!-- film info -->
  <div v-if="film" style="margin: 15px">
    <div class="row">
      <div>
        <img class="poster-image" :src="film.poster_url" >
        <div class="film-details">
          <LanguageIcon class="language-icon" v-if="!['en','xx'].includes(film.original_language)" :language_code="film.original_language" :height="15"></LanguageIcon>
          <span style="font-weight: 500;">{{ film.genreText(' • ') }} • {{ film.runtimeText() }}</span>

          <div>
            {{film.tmdb?.overview}}
            <a v-if="film.imdbLink()" class="imdb-link" :href="film.imdbLink()" title='IMDB' target='blank'>
              <span style="font-size: 0.75em;" class='fa-stack'>
                <i style="color: black;" class='fa fa-stack-2x fa-square'></i>
                <i style="color: #F5C518;" class='fa fa-stack-2x fa-imdb'></i>
                <i class="fa fa-stack-1x fa-external-link"></i>
              </span>
            </a>
          </div>
        </div>
      </div>

<!--
      <div class="film-footnotes" v-if="film.footnotes">
        <ul>
          <li v-for="(footnote, index) in film.footnotes" :key="`footnote_${index+1}`">
            {{footnote}}
          </li>
        </ul>
      </div>
-->

      <ViewedByFriendsCard :film_id="film_id"></ViewedByFriendsCard>

      <div style="padding: 10px 0 10px 0;">
        <ViewChoiceIcons
            :membership_required="film.doStreamingProvidersRequireMembership()"
            :subscription_providers="film.getSubscriptionProviders()"
            :rental_and_purchase_providers="film.getRentalAndPurchaseProviders()"
            :free_providers="film.getFreeAndAdsProviders()"
            @learn-about-membership="$emit('learn-about-membership')"
        ></ViewChoiceIcons>
      </div>

      <hr>

      <div class="nomination-list">
        <ul> <!-- nomination list -->
          <li v-for="(nomination, index) in film.nominations" :key="index" :class="nomination.won? 'li_win' : nomination.unofficial? 'li_unofficial_nom' : 'li_nom'">
            <small>
              <a @click="showCategoryDetails(nomination.category.name, nomination.ceremony)" >
                {{ nomination.category.name }} | {{getCeremonyText(nomination.ceremony)}}
              </a>
              <br>
              <div class="nominee-list">{{ nomination.nominee_display_text }}<sup v-if="footnotes" class="nomination-footnote">{{footnoteNumString(nomination.footnotes)}}</sup></div>
            </small>
          </li>
        </ul>
      </div>

      <router-link v-if="userStore.isAdmin()" class="edit-film-link" :to="{name: 'AdminEditFilm', params: {filmId: this.film._id}}">
        <i class="fa fa-pencil-square-o"></i>
      </router-link>

    </div>
  </div>

  <!-- placeholder glow -->
  <div v-else :class="{ 'modal-body': true, 'placeholder-glow': isLoading }" style="margin: 15px; text-align: left;">
    <div class="row" style="padding-bottom: 20px;">
      <div class="col-auto">
        <div class="placeholder poster-image"></div>
      </div>
      <div class="col small">
        <span class="placeholder col-11"></span>
        <span class="placeholder col-10"></span>
        <span class="placeholder col-10"></span>
        <span class="placeholder col-11"></span>
        <span class="placeholder col-12"></span>
        <span class="placeholder col-10"></span>
        <span class="placeholder col-8"></span>
      </div>
    </div>
    <hr>

    <div class="row">
      <div class="nomination-list">
        <small class="placeholder col-10"></small>
        <small class="placeholder col-8"></small>
        <small class="placeholder col-11"></small>
        <small class="placeholder col-8"></small>
      </div>
    </div>
  </div>

  <!-- tell visitors to sign in for more movie info -->
  <div v-if="!userStore.isLoggedIn()" class="sign-in-message">
    <img :src="`${$imagePathBase}logo-gold.svg`" alt="Logo" class="logo-image">
    <span class="call-to-action">
      <router-link class="login-link" :to="{name: 'SignUpPage'}">Sign up</router-link>
      or <router-link class="login-link" :to="{name: 'LogInPage'}">Sign in</router-link>
    </span>
    <br>to see all the details about this film and its nominations.
  </div>

</template>

<script>
import axios from "axios";
import {Film} from "@/film";
import ViewChoiceIcons from "@/components/modal/ViewChoiceIcons.vue";
import LanguageIcon from "@/components/LanguageIcon.vue";
import ViewedByFriendsCard from "@/components/friends/cards/ViewedByFriendsCard.vue";
import {userStore} from "@/userStore";
import analytics from "@/analytics";

export default {
  name: "ModalBodyForFilm",
  components: {ViewedByFriendsCard, LanguageIcon, ViewChoiceIcons},
  props: ['film_name', 'film_id', 'link_location'],
  inject: ['closeModal', 'showCategoryDetails'],
  emits: ['show-category', 'film-details-loaded'],

  data() {
    return {
      time_mounted: Date.now(),
      film: null,
      footnotes: [],
      viewed_by_friends: [],
      axiosAbortcontroller: null,
    }
  },

  async mounted() {
    if ( userStore.isLoggedIn() ) {
      return this.retrieveFilmDetails();
    }
  },

  unmounted() {
    // if we're mid-download, cancel it
    if ( this.axiosAbortcontroller ) {
      this.axiosAbortcontroller.abort();
      this.axiosAbortcontroller = null;

      // if the download happened, send an analytics event with how much time it was displayed
    } else if (this.film) {

      //console.log(`this.link_location = ${this.link_location}`)
      const elapsedTime = Date.now() - this.time_mounted;
      analytics.sendEvent('film_details_viewed', {
        film_id: this.film._id,
        film: `${this.film.name} (${this.film.year})`,
        link_location: this.link_location,
        seconds: elapsedTime / 1000,
      });
    }
  },

  computed: {
    userStore() {
      return userStore
    },
    isLoading() {
      return !!this.axiosAbortcontroller;
    }
  },

  methods: {
    async retrieveFilmDetails() {
      this.axiosAbortcontroller = new AbortController();

      return axios.get(`/api/film/${this.film_id}/details`, { signal: this.axiosAbortcontroller.signal } )
          .then(response => {

            this.time_mounted = Date.now();
            this.film = new Film(response.data);

            // gather the film and nomination footnotes, ensuring there are no dups
            this.footnotes = [ ...new Set([
              ...this.film.footnotes,
              ...this.film.nominations.reduce((accFootnotes, curNom) => {
                return [...accFootnotes, ...curNom.footnotes];
              }, [])
            ])];

            // tell my parent this film is done loading
            this.$emit('film-details-loaded', this.film, this.footnotes);
          })

          .catch(err => {
            if ( err.code !== 'ERR_CANCELED') {
              //console.log(err);
              analytics.notifyBugsnagOfError(err);
            }
          })
          .finally(() => {
            this.axiosAbortcontroller = null;
          });
    },
    getCeremonyText(_ceremony) {
      return Film.getCeremonyHeaderText(_ceremony);
    },
    onClickImdbLink() {
      analytics.sendEvent('imdb_link_opened', {
        film_id: this.film._id,
        film: `${this.film.name} (${this.film.year})`,
        link: this.film.imdbLink()
      });
    },

    footnoteNumString(_footnotes) {
      return _footnotes.reduce((accNumbers, curFootnote) => {
        const footnoteNum = 1 + this.footnotes?.indexOf(curFootnote);
        if ( footnoteNum > 0 ) {
          return [...accNumbers, footnoteNum];
        }
        return accNumbers;
      }, []).join('\u200A,\u200A') || ''; // hair-spaces around comma make it more readable
    }
  }
}
</script>

<style scoped>

  .modal-title {
    padding-left: 10px;
  }
  .poster-image {
    float: left;
    margin: 0 15px 0 0;
    width:100px!important;
    height:150px!important;
    object-fit:cover;
  }

  .film-details {
    text-align: left;
  }

  /*
  .film-footnotes {

    color: var(--bs-secondary-color);
    float: right;
    width: fit-content;

    margin-top: 0.75em;
    margin-right: 0;
    margin-left: auto;
    display: block;
    font-size: smaller;

    ul {
      padding-left: 1em;
      width: fit-content;
      margin: 0;

      li {
        margin-top: 0.15em;
        width: fit-content;
        text-align: left;
        list-style: none;
        font-style: italic;

        &::marker {
          content: "*\200A"; !*asterisk with a 'hair space' after it*!
        }
      }
    }
  }
  */

  .language-icon {
    padding-right: 5px;
  }

  a {
    text-decoration: inherit;
    color: inherit;
    cursor: pointer;
  }
  a:hover {
    color: var(--bs-link-hover-color)!important;
  }

  .nomination-list {
    font-size: small;

    .nominee-list {
      font-weight: 350;
      padding-left: 0.3em;
      font-style: italic;

      .nomination-footnote {
        margin-left: 0.1em;
        font-style: normal;
        font-size: 80%;
        color: var(--bs-secondary-color);
      }
    }

    ul {
      padding-left: 10px;
    }

    li {
      text-align: left;
      list-style: none;
      margin: 10px 0 0 0;
      padding: 0px 0 0px 20px;
      background-repeat: no-repeat;
      background-position-x: 0px;
      background-position-y: 4px;
      background-size: 14px;
    }
  }

  .edit-film-link {
    text-align: right;
    color: crimson!important;
    &:hover {
      color: red!important;
    }
  }

  .imdb-link {
    margin-top: 5px;
    position: relative;

    .fa-external-link {
      position: absolute;
      opacity: 80%;
      bottom: 7px;
      left: 8px;
      /*padding-bottom: 7px;
      padding-left: 14px;*/
    }
  }

  /**
   Each placeholders' parent will be 'placeholder-glow' while actively loading.
   If we're NOT actively loading, we want to fade-out the placeholder, and keep
   the cursor from going to its waiting state.
  **/
  .placeholder:not(.placeholder-glow *) {
    cursor: initial;
    opacity: 20%;
  }

  .sign-in-message {

    animation: fadeIn 0.5s;

    text-align: center;
    position: absolute;
    top: 25%;
    left: 50%;
    transform: translate(-50%, 0);

    background-color: var(--mol-color-gold-background);
    border-style: solid;
    border-radius: 5px;
    border-color: var(--mol-color-gold);
    border-width: medium;
    box-shadow: 0 3px 10px -5px grey;
    padding: 10px;

    img {
      position: absolute;
      top: 5px;
      left: 5px;

      height: 20px;
    }

    .call-to-action {
      font-size: 110%;
    }

    .login-link {
      font-weight: 500;
      color: var(--bs-link-hover-color);
    }

  }

  @keyframes fadeIn {
    0% {opacity: 0%;}
    50% {opacity: 0%;}
    100% {opacity: 100%;}
  }

</style>